import type {
  CreateWorkLinkRequest,
  LinkableTargetRefName,
  RagStatus,
  WorkLink,
  WorkLinkQuery,
  WorkLinkTargetRefName,
  WorkLinkWithStatusPage,
} from "@playbookai/types";
import { apiCall } from "./http.js";
import type { ApiClientConfig } from "./client-config.js";

/**
 * Live playbook-be client for the platform work-link surface (PB-239 FE against
 * the PB-288/PB-290 API). Not a Hoshin endpoint: `/v1/work-links` is
 * platform-owned, because the same link joins strategy to SOLVE/VSM work from
 * either side.
 *
 * It lived in `hoshin-feature` while Hoshin was the only caller, which the note
 * above already flagged as wrong. SOLVE now reads the same endpoint to show what
 * strategy it is linked FROM, and a `*-feature` package may not import a sibling
 * feature (ADR-0009), so the client belongs here — one client for one
 * platform capability, per CLAUDE.md's reuse rule.
 *
 * Search is deliberately not a work-links endpoint — none exists, and none is
 * needed: the picker searches each work kind through that module's own list
 * endpoint, which already does server-side `q` filtering with a cursor. That
 * keeps the "never enumerate the pool" rule enforced by the server rather than
 * by the client.
 */
const WORK_LINKS_PATH = "/v1/work-links";
const SOLVES_PATH = "/v1/solves";
const VSMS_PATH = "/v1/vsms";
const HOSHIN_TTIS_PATH = "/v1/hoshin-ttis";
const HOSHIN_PLANS_PATH = "/v1/hoshin-plans";
const KPIS_PATH = "/v1/kpis";

/**
 * The one `include` token the work-links contract accepts today (PB-517). It
 * enriches each link with its target's live status, resolved at read time —
 * opening the view IS the refresh, so there is no background sync to wait on.
 */
const WORK_STATUS_INCLUDE = "workStatus";

/** Result cap per kind. The server bounds each page; this bounds the merge. */
export const WORK_SEARCH_LIMIT = 20;

/** One work item a user can link, as returned by a module's list endpoint. */
export interface WorkSearchHit {
  readonly refName: LinkableTargetRefName;
  readonly refId: string;
  readonly title: string;
  readonly reference?: string;
  readonly owner?: { readonly name: string };
}

export interface WorkSearchPage {
  readonly items: readonly WorkSearchHit[];
  /** True when at least one kind reported more results than were returned. */
  readonly hasMore: boolean;
  /**
   * Kinds whose endpoint rejected. Empty on a healthy search.
   *
   * Separate from `hasMore` on purpose. A rejected kind used to be OR-ed into
   * that flag, which made truncation and a partial outage indistinguishable —
   * and the truncation copy the flag drives ("keep typing to narrow") is
   * actively wrong advice when a whole kind is down, because narrowing cannot
   * bring it back. The intent behind that term was right; the mechanism said
   * the wrong thing.
   */
  readonly failedKinds: readonly LinkableTargetRefName[];
}

export interface WorkSearchQuery {
  readonly query: string;
  /** Empty means "both kinds". */
  readonly kinds: readonly LinkableTargetRefName[];
  readonly limit?: number;
}

/** Result cap per page of the plan step. The server bounds it too. */
export const STRATEGY_PLAN_SEARCH_LIMIT = 20;

/**
 * One Hoshin plan the caller may open, from `GET /v1/hoshin-plans` (PB-5366).
 *
 * Deliberately carries NO `refName`: a plan is not a link end. It is the
 * CONTAINER the target picker drills into on its first step, and giving it a
 * ref pair would make it indistinguishable from something a create call could
 * be handed. `refId` is the plan id, which is all the second step needs.
 *
 * `orgName` and `fiscalYear` travel raw rather than pre-joined into one line:
 * composing display copy is the host's job, and an absent part must not leave a
 * padded separator behind.
 */
export interface StrategyPlanHit {
  readonly refId: string;
  readonly title: string;
  readonly orgName?: string;
  readonly fiscalYear?: string;
}

export interface StrategyPlanPage {
  readonly items: readonly StrategyPlanHit[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
}

export interface StrategyPlanQuery {
  /** Case-insensitive substring of the plan's name / org name. Blank = no filter. */
  readonly query?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/** The fields of a plan summary this step actually reads. */
interface PlanSummaryProjection {
  readonly id: string;
  readonly name: string;
  readonly orgName?: string;
  readonly fiscalYear?: string;
}

/**
 * Plans the caller may open, through the Hoshin module's OWN list endpoint
 * (`GET /v1/hoshin-plans`), which already does server-side `q` filtering with a
 * cursor — so no plan-search endpoint is invented here, exactly as
 * {@link workLinksApi.searchWork} explains.
 *
 * This is the FIRST step of attaching a Hoshin target: search a plan, then see
 * that plan's targets. Adding a target without naming its plan is what made two
 * same-named targets indistinguishable, and it offered the whole tenant's
 * targets at once when the reader already knew which plan they meant.
 *
 * Visibility is the endpoint's own members-only predicate, so a plan the caller
 * cannot open is simply absent — the picker can never name one to a stranger.
 */
export async function searchStrategyPlans(
  config: ApiClientConfig,
  { query, limit, cursor }: StrategyPlanQuery = {},
): Promise<StrategyPlanPage> {
  const params: Record<string, string> = {
    limit: String(limit ?? STRATEGY_PLAN_SEARCH_LIMIT),
  };
  const trimmed = query?.trim();
  if (trimmed) params["q"] = trimmed;
  if (cursor) params["cursor"] = cursor;

  const page = await apiCall<Page<PlanSummaryProjection>>(
    config,
    "GET",
    HOSHIN_PLANS_PATH,
    undefined,
    params,
  );
  return {
    items: page.data.map<StrategyPlanHit>((plan) => ({
      refId: plan.id,
      title: plan.name,
      ...(plan.orgName ? { orgName: plan.orgName } : {}),
      ...(plan.fiscalYear ? { fiscalYear: plan.fiscalYear } : {}),
    })),
    nextCursor: page.nextCursor,
    hasMore: page.hasMore,
  };
}

/** Result cap per page of the SOLVE list. The server bounds it too. */
export const SOLVE_SEARCH_LIMIT = 20;

/** One SOLVE the caller may open, from `GET /v1/solves`. */
export interface SolveHit {
  readonly refId: string;
  readonly title: string;
  /** Human-facing id, e.g. `SOLVE-42`. */
  readonly reference?: string;
  readonly ownerName?: string;
}

export interface SolvePage {
  readonly items: readonly SolveHit[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
}

export interface SolveQuery {
  /** Case-insensitive substring of the SOLVE's title. Blank = no filter. */
  readonly query?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/**
 * SOLVEs the caller may open, through SOLVE's OWN list endpoint
 * (`GET /v1/solves`) — the same endpoint {@link workLinksApi.searchWork} asks
 * for its `solve` kind, and the same rows.
 *
 * It exists BESIDE that search rather than inside it because of the one thing
 * that search cannot carry: a cursor. `searchWork` reads one list per requested
 * kind and merges them, and a keyset position describes ONE list — the reason
 * {@link StrategyKpiPage} is cursorless. A caller that wants a single kind
 * wants a single list, and a single list can page honestly. Nothing else
 * differs: same path, same server-side `q` filter, same members-only
 * visibility, so a SOLVE the caller cannot open is simply absent.
 */
export async function searchSolves(
  config: ApiClientConfig,
  { query, limit, cursor }: SolveQuery = {},
): Promise<SolvePage> {
  const params: Record<string, string> = {
    limit: String(limit ?? SOLVE_SEARCH_LIMIT),
  };
  const trimmed = query?.trim();
  if (trimmed) params["q"] = trimmed;
  if (cursor) params["cursor"] = cursor;

  const page = await apiCall<Page<SolveSummary>>(
    config,
    "GET",
    SOLVES_PATH,
    undefined,
    params,
  );
  return {
    items: page.data.map<SolveHit>((solve) => ({
      refId: solve.id,
      title: solve.title,
      ...(solve.reference ? { reference: solve.reference } : {}),
      ...(solve.owner?.name ? { ownerName: solve.owner.name } : {}),
    })),
    nextCursor: page.nextCursor,
    hasMore: page.hasMore,
  };
}

/** Result cap per page of the strategy picker. The server bounds it too. */
export const STRATEGY_TTI_SEARCH_LIMIT = 20;

/**
 * One Target to Improve a user can link to, from `GET /v1/hoshin-ttis`
 * (PB-5366). Shaped like {@link WorkSearchHit} — `refName` + `refId` are what a
 * create call needs — but the STRATEGY end, so `refName` is the single literal
 * `hoshin_target` rather than a work kind.
 */
export interface StrategyTtiHit {
  readonly refName: "hoshin_target";
  readonly refId: string;
  readonly title: string;
  readonly status: RagStatus;
  /** The plan holding it, so two same-named targets are distinguishable. */
  readonly planName: string;
}

export interface StrategyTtiPage {
  readonly items: readonly StrategyTtiHit[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
}

export interface StrategyTtiQuery {
  /** Case-insensitive substring of the target's title. Blank means no filter. */
  readonly query?: string;
  readonly limit?: number;
  readonly cursor?: string;
  /**
   * The plan the picker has drilled into. A FILTER, never a gate: the server
   * ANDs it onto its own visibility predicate, so a plan the caller cannot view
   * answers an empty page rather than confirming it exists.
   */
  readonly planId?: string;
  /**
   * Omit the targets already linked to this work item, so a picker opened from
   * a SOLVE never re-offers what that SOLVE is already linked to. Sent as a
   * PAIR — the server answers `400` to half of one — which is why it is modelled
   * as one optional object rather than two optional params.
   */
  readonly excludeLinkedTo?: {
    readonly refName: WorkLinkTargetRefName;
    readonly refId: string;
  };
}

/** The server's own row shape for a picker row (`HoshinTtiSummary`). */
export interface HoshinTtiSummary {
  readonly id: string;
  readonly title: string;
  readonly status: RagStatus;
  readonly planName: string;
}

/** A cursor page of them, exactly as `GET /v1/hoshin-ttis` answers. */
export type HoshinTtiListPage = Page<HoshinTtiSummary>;

interface ListHoshinTtisBaseParams {
  readonly limit?: number;
  readonly cursor?: string;
  /** Case-insensitive substring of the target's title. */
  readonly q?: string;
  /**
   * Narrow to ONE plan — the picker's second step. Composes with `q`, the
   * cursor and the exclusion pair; it is one more `AND` server-side, not an
   * authorization input.
   */
  readonly planId?: string;
}

/** Both exclusion fields or neither: partial pairs are invalid at the API. */
export type ListHoshinTtisParams = ListHoshinTtisBaseParams &
  (
    | {
        readonly excludeLinkedRefName: WorkLinkTargetRefName;
        readonly excludeLinkedRefId: string;
      }
    | {
        readonly excludeLinkedRefName?: never;
        readonly excludeLinkedRefId?: never;
      }
  );

/**
 * `GET /v1/hoshin-ttis` — every Target to Improve the caller may LINK, without
 * naming a plan (PB-5366).
 *
 * The one client for this route, shared by every surface that links work to
 * strategy: SOLVE and VSM both open a picker over it, and a second
 * implementation is how two pickers end up disagreeing about the exclusion
 * pair. Exposed as a plain function beside {@link workLinksApi} because it is a
 * Hoshin read, not a work-link write — it lives here because the work-link
 * picker is its only reason to exist.
 *
 * The server requires an editable, writable plan per row. A viewer, a closed
 * plan or a plan the caller cannot open contributes nothing and is never named.
 * Archived targets and targets in a soft-deleted plan are omitted too.
 */
export async function listHoshinTtis(
  config: ApiClientConfig,
  params: ListHoshinTtisParams = {},
): Promise<HoshinTtiListPage> {
  const query: Record<string, string> = {
    limit: String(params.limit ?? STRATEGY_TTI_SEARCH_LIMIT),
  };
  if (params.q?.trim()) query["q"] = params.q.trim();
  if (params.planId) query["planId"] = params.planId;
  if (params.cursor) query["cursor"] = params.cursor;
  // Forward each supplied field even for an untyped caller: the API rejects
  // incomplete pairs with 400 instead of silently returning unfiltered rows.
  if (params.excludeLinkedRefName !== undefined) {
    query["excludeLinkedRefName"] = params.excludeLinkedRefName;
  }
  if (params.excludeLinkedRefId !== undefined) {
    query["excludeLinkedRefId"] = params.excludeLinkedRefId;
  }
  return apiCall<HoshinTtiListPage>(
    config,
    "GET",
    HOSHIN_TTIS_PATH,
    undefined,
    query,
  );
}

/** Result cap for the KPI picker, matching the TTI one. */
export const STRATEGY_KPI_SEARCH_LIMIT = 20;

/**
 * Work-link candidates are active only (ADR-0033). Hoshin's TTI-to-KPI
 * measurement-identity picker has a different lifecycle rule (ADR-0040).
 */
export const STRATEGY_KPI_BROWSE_LIFECYCLES = ["active"] as const;

/**
 * One configured KPI a work item may link itself to (PB-5366).
 *
 * Shaped like {@link StrategyTtiHit} so a picker renders both the same way:
 * `refName` + `refId` are what a create call needs, and `refName` is the single
 * literal `kpi` rather than a work kind.
 */
export interface StrategyKpiHit {
  readonly refName: "kpi";
  readonly refId: string;
  readonly title: string;
  /** The unit it is measured in, so two same-named KPIs are distinguishable. */
  readonly unit?: string;
  readonly lifecycleStatus: "active";
}

/**
 * Deliberately CURSORLESS, unlike {@link StrategyPlanPage} and
 * {@link StrategyTtiPage}.
 *
 * The picker has a finite result cap and asks users to narrow by name. It does
 * not expose a cursor; the active-only KPI list may have more matching rows.
 */
export interface StrategyKpiPage {
  readonly items: readonly StrategyKpiHit[];
  readonly hasMore: boolean;
}

export interface StrategyKpiQuery {
  /** Case-insensitive substring of the KPI's name/description. Blank = no filter. */
  readonly query?: string;
  readonly limit?: number;
}

/** The fields of `KpiSummary` this picker actually reads. */
interface KpiSummaryProjection {
  readonly kpiId: string;
  readonly name: string;
  readonly unit?: string;
}

/**
 * KPIs this caller may link work to, through the KPI module's OWN list endpoint
 * (`GET /v1/kpis`), which already does server-side `q` filtering with a cursor —
 * so no work-links search endpoint is invented here, exactly as
 * {@link searchWork} explains.
 *
 * Browses `active` only, matching the work-link create gate (ADR-0033).
 * Hoshin's TTI-to-KPI measurement-identity picker may also browse drafts;
 * that is a different relation with a different lifecycle rule (ADR-0040).
 *
 * There is deliberately no `excludeLinkedTo`: unlike `/v1/hoshin-ttis`, the KPI
 * list has no exclusion parameter, and adding one would put a work-links
 * concern inside the KPI module. The caller marks its already-linked rows from
 * its own link list instead — the same answer the value-stream tab gives, and
 * the more honest one: the row says "Linked", it does not vanish.
 *
 * Authorization is the KPI module's: ADR-0033 makes a KPI's work-link end
 * VIEW-level, so any tenant member who can SEE a KPI may link it. That is why a
 * SOLVE collaborator can use this without any KPI administration right.
 */
export async function searchStrategyKpis(
  config: ApiClientConfig,
  { query, limit }: StrategyKpiQuery = {},
): Promise<StrategyKpiPage> {
  const params: Record<string, string> = {
    limit: String(limit ?? STRATEGY_KPI_SEARCH_LIMIT),
    lifecycleStatus: STRATEGY_KPI_BROWSE_LIFECYCLES[0],
  };
  const trimmed = query?.trim();
  if (trimmed) params["q"] = trimmed;
  const page = await apiCall<Page<KpiSummaryProjection>>(
    config,
    "GET",
    KPIS_PATH,
    undefined,
    params,
  );
  return {
    items: page.data.map((kpi) => ({
      refName: "kpi" as const,
      refId: kpi.kpiId,
      title: kpi.name,
      lifecycleStatus: "active" as const,
      ...(kpi.unit ? { unit: kpi.unit } : {}),
    })),
    hasMore: page.hasMore,
  };
}

/** Minimal projections of the two work summaries — only what the picker shows. */
interface SolveSummary {
  readonly id: string;
  readonly title: string;
  readonly reference?: string;
  readonly owner?: { readonly name?: string };
}
interface VsmSummary {
  readonly id: string;
  readonly title?: string;
  readonly name?: string;
  readonly reference?: string;
  readonly owner?: { readonly name?: string };
}
interface Page<T> {
  readonly data: readonly T[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
}

interface WorkSearchKindPage {
  readonly items: readonly WorkSearchHit[];
  readonly hasMore: boolean;
}

function interleave<T>(groups: readonly (readonly T[])[]): T[] {
  const merged: T[] = [];
  const longest = Math.max(0, ...groups.map((group) => group.length));
  for (let index = 0; index < longest; index += 1) {
    for (const group of groups) {
      const item = group[index];
      if (item !== undefined) merged.push(item);
    }
  }
  return merged;
}

export const workLinksApi = {
  /**
   * Links in which the entity participates on **either** side. The contract
   * takes one ref pair rather than a source/target choice, so a Hoshin item and
   * a SOLVE both read their links the same way.
   */
  async listWorkLinks(
    config: ApiClientConfig,
    query: WorkLinkQuery,
  ): Promise<WorkLinkWithStatusPage> {
    const params: Record<string, string> = {
      refName: query.refName,
      refId: query.refId,
      // PB-517. The server rejects unknown tokens with 400 rather than ignoring
      // them, so this must stay in step with the contract's accepted set.
      include: WORK_STATUS_INCLUDE,
    };
    if (query.limit !== undefined) params["limit"] = String(query.limit);
    if (query.cursor !== undefined) params["cursor"] = query.cursor;
    return apiCall<WorkLinkWithStatusPage>(
      config,
      "GET",
      WORK_LINKS_PATH,
      undefined,
      params,
    );
  },

  /**
   * Create a link. The server owns `contextSnapshot`, `contextVersion`,
   * `relationship` and `scopeRef` — a client cannot assert its own "why" — and
   * derives tenant + actor from the JWT. Re-posting an existing pair is
   * idempotent (`200` with the existing link), so the caller needs no
   * duplicate check of its own.
   */
  async createWorkLink(
    config: ApiClientConfig,
    input: CreateWorkLinkRequest,
  ): Promise<WorkLink> {
    return apiCall<WorkLink>(config, "POST", WORK_LINKS_PATH, input);
  },

  /** Unlink — a reversible soft delete server-side; `204`, no body. */
  async deleteWorkLink(config: ApiClientConfig, linkId: string): Promise<void> {
    await apiCall<void>(
      config,
      "DELETE",
      `${WORK_LINKS_PATH}/${encodeURIComponent(linkId)}`,
    );
  },

  /**
   * Targets to Improve the caller may link to, from `GET /v1/hoshin-ttis`
   * (PB-5366) — the STRATEGY end of the picker a SOLVE or a value stream opens.
   *
   * Server-side in every respect that matters: `q` filters, the page is bounded
   * and cursored, and visibility is the plan predicate applied per row, so the
   * list can only ever offer targets the caller can actually open. The
   * exclusion pair is forwarded so an already-linked target never reaches the
   * UI at all — filtering client-side would leak the existence of targets the
   * caller may not see on a later page.
   */
  async searchStrategyTtis(
    config: ApiClientConfig,
    {
      query = "",
      limit = STRATEGY_TTI_SEARCH_LIMIT,
      cursor,
      planId,
      excludeLinkedTo,
    }: StrategyTtiQuery = {},
  ): Promise<StrategyTtiPage> {
    const page = await listHoshinTtis(config, {
      limit,
      ...(query.trim() ? { q: query.trim() } : {}),
      ...(planId ? { planId } : {}),
      ...(cursor ? { cursor } : {}),
      // Both or neither — see {@link StrategyTtiQuery.excludeLinkedTo}.
      ...(excludeLinkedTo
        ? {
            excludeLinkedRefName: excludeLinkedTo.refName,
            excludeLinkedRefId: excludeLinkedTo.refId,
          }
        : {}),
    });
    return {
      items: page.data.map<StrategyTtiHit>((tti) => ({
        refName: "hoshin_target" as const,
        refId: tti.id,
        title: tti.title,
        status: tti.status,
        planName: tti.planName,
      })),
      nextCursor: page.nextCursor,
      hasMore: page.hasMore,
    };
  },

  /**
   * Search linkable work by name, one call per requested kind.
   *
   * Both list endpoints are members-only and tenant-scoped server-side, so the
   * picker can only ever offer work the user may actually see. Neither returns
   * a total match count, so `hasMore` is reported instead of a fabricated
   * total — the UI says "more matches" rather than inventing a number.
   */
  async searchWork(
    config: ApiClientConfig,
    { query, kinds, limit = WORK_SEARCH_LIMIT }: WorkSearchQuery,
  ): Promise<WorkSearchPage> {
    const wanted = kinds.length > 0 ? kinds : (["solve", "vsm"] as const);
    const params: Record<string, string> = { limit: String(limit) };
    if (query.trim()) params["q"] = query.trim();

    const settled = await Promise.allSettled(
      wanted.map(async (kind) => {
        if (kind === "solve") {
          const page = await apiCall<Page<SolveSummary>>(
            config,
            "GET",
            SOLVES_PATH,
            undefined,
            params,
          );
          return {
            items: page.data.map<WorkSearchHit>((s) => ({
              refName: "solve" as const,
              refId: s.id,
              title: s.title,
              ...(s.reference ? { reference: s.reference } : {}),
              ...(s.owner?.name ? { owner: { name: s.owner.name } } : {}),
            })),
            hasMore: page.hasMore,
          };
        }
        const page = await apiCall<Page<VsmSummary>>(
          config,
          "GET",
          VSMS_PATH,
          undefined,
          params,
        );
        return {
          items: page.data.map<WorkSearchHit>((v) => ({
            refName: "vsm" as const,
            refId: v.id,
            // `name` FIRST, matching `VsmResponse` and the backend's
            // `VsmTargetResolver.displayName`. Both columns exist through a
            // rename still in flight, and this side resolved them in the
            // OPPOSITE order — so a VSM carrying two different values was
            // labelled one way in the picker and another way in the row it
            // became once linked.
            title: v.name ?? v.title ?? "Untitled value stream",
            ...(v.reference ? { reference: v.reference } : {}),
            ...(v.owner?.name ? { owner: { name: v.owner.name } } : {}),
          })),
          hasMore: page.hasMore,
        };
      }),
    );

    // `settled` is positionally aligned with `wanted`, so the index names the
    // kind that failed. Collected here rather than inferred later: once the
    // fulfilled pages are merged there is nothing left to identify a kind that
    // returned nothing because it was down.
    const results: WorkSearchKindPage[] = [];
    const failedKinds: LinkableTargetRefName[] = [];
    for (const [index, result] of settled.entries()) {
      if (result.status === "fulfilled") results.push(result.value);
      else failedKinds.push(wanted[index] as LinkableTargetRefName);
    }
    if (results.length === 0) {
      const failure = settled.find(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected",
      );
      // Guaranteed to be an `Error`, with the original kept as `cause`. The raw
      // reason went out before, and a rejection carrying `undefined` or a
      // non-Error reached callers that read `error.message` (`useWorkLinks`) —
      // so a failed search surfaced as a TypeError instead of its own message.
      throw failure?.reason instanceof Error
        ? failure.reason
        : new Error("Couldn’t search existing work.", {
            cause: failure?.reason,
          });
    }

    const merged = interleave(results.map((result) => result.items));

    return {
      items: merged.slice(0, limit),
      // Truncation only. A rejected kind is reported by `failedKinds`.
      hasMore: results.some((r) => r.hasMore) || merged.length > limit,
      failedKinds,
    };
  },
};
